import { useEffect, useMemo } from 'react'

import { useAppSelector } from 'store'
import { getMapComponent } from 'utils'
import { TChannelVariant } from 'interfaces'
import { useUnityHubCreateChannel, TUseUnityHubCreateChannel } from 'App/components/UnityHub'

import {
  CommunityCreateChannelMainStep,
  TCommunityCreateChannelMainStepProps
} from '../CommunityCreateChannelMainStep'
import {
  CommunityCreateChannelMembersStep,
  TCommunityCreateChannelMembersStepProps
} from '../CommunityCreateChannelMembersStep'
import { useMembersRequest } from '../useMembersRequest'

enum steps {
  MAIN = 0,
  MEMBERS = 1,
  CHANEL_MANAGER = 2
}

const MAX_SELECTED_CHANNEL_MANAGER = 5

type TStepsMapProps = Pick<
  TCommunityCreateChannelMainStepProps,
  | 'channelId'
  | 'setName'
  | 'channelManagers'
  | 'isEdit'
  | 'loading'
  | 'communityId'
  | 'variant'
  | 'onEditSuccess'
  | 'dirty'
  | 'isPublic'
  | 'setIsPublic'
  | 'initialValues'
> &
  Pick<
    TCommunityCreateChannelMembersStepProps,
    'setChannelMembers' | 'channelMembers' | 'setChannelManagers'
  > & {
    name: string
    handleOpenMain: () => void
    handleOpenChanelManager: () => void
    handleOpenMembers: () => void
  }

const STEPS_MAP = new Map<steps, (props: TStepsMapProps) => JSX.Element>()
  .set(
    steps.MAIN,
    ({
      name,
      communityId,
      channelId,
      channelMembers,
      channelManagers,
      isEdit,
      loading,
      variant,
      dirty,
      isPublic,
      handleOpenChanelManager,
      handleOpenMembers,
      setName,
      setIsPublic,
      onEditSuccess,
      initialValues
    }) => (
      <CommunityCreateChannelMainStep
        dirty={dirty}
        variant={variant}
        communityId={communityId}
        name={name}
        channelId={channelId}
        channelMembers={channelMembers}
        channelManagers={channelManagers}
        isEdit={isEdit}
        isPublic={isPublic}
        loading={loading}
        initialValues={initialValues}
        setName={setName}
        setIsPublic={setIsPublic}
        onEditSuccess={onEditSuccess}
        onOpenMembers={handleOpenMembers}
        onOpenChanelManager={handleOpenChanelManager}
      />
    )
  )
  .set(
    steps.MEMBERS,
    ({
      channelId,
      channelMembers,
      communityId,
      channelManagers,
      handleOpenMain,
      setChannelMembers,
      setChannelManagers
    }) => (
      <CommunityCreateChannelMembersStep
        variant="members"
        channelManagerIds={channelManagers?.map((manager) => manager.id)}
        communityId={communityId}
        channelMembers={channelMembers}
        markChatIds={channelId ? [channelId] : []}
        onBack={handleOpenMain}
        setChannelMembers={setChannelMembers}
        setChannelManagers={setChannelManagers}
      />
    )
  )
  .set(
    steps.CHANEL_MANAGER,
    ({ communityId, channelManagers, handleOpenMain, setChannelManagers }) => (
      <CommunityCreateChannelMembersStep
        variant="manager"
        maxSelected={MAX_SELECTED_CHANNEL_MANAGER}
        channelManagerIds={channelManagers?.map((manager) => manager.id)}
        communityId={communityId}
        channelMembers={channelManagers ? channelManagers : []}
        onBack={handleOpenMain}
        setChannelManagers={setChannelManagers}
      />
    )
  )

export type TCommunityCreateChannelDialogProps = Pick<
  TUseUnityHubCreateChannel,
  'channelId' | 'channelName' | 'isPublic'
> &
  Pick<TCommunityCreateChannelMainStepProps, 'onEditSuccess'> & {
    communityId: string
    variant?: Extract<TChannelVariant, 'chat' | 'news'>
  }

export const CommunityCreateChannelDialog = ({
  communityId,
  channelName,
  onEditSuccess,
  channelId = '',
  isPublic: channelIsPublic,
  variant = 'chat'
}: TCommunityCreateChannelDialogProps) => {
  const {
    name,
    channelMembers,
    channelManagers,
    step,
    isEdit,
    dirty,
    isPublic,
    setIsPublic,
    handleOpenMain,
    handleOpenMembers,
    handleOpenChanelManager,
    setName,
    setChannelMembers,
    setChannelManagers
  } = useUnityHubCreateChannel({
    channelName,
    channelId,
    isPublic: channelIsPublic,
    maxSelectedChannelManager: MAX_SELECTED_CHANNEL_MANAGER
  })

  const room = useAppSelector((state) => state.chat.chatRooms.list[channelId])

  const initialValues = useMemo(
    () => ({ name: channelName || '', isPublic: channelIsPublic || false }),
    [channelName, channelIsPublic]
  )

  const { isLoading, accounts } = useMembersRequest({
    variant,
    channelId,
    communityId,
    isEdit,
    setChannelMembers
  })

  useEffect(() => {
    if (isEdit && accounts?.length) {
      setChannelManagers(room.channelManagers ? room.channelManagers : undefined, true)
    }
  }, [accounts?.length, isEdit, room?.channelManagers, setChannelManagers])

  return (
    <>
      {getMapComponent(STEPS_MAP, step, {
        dirty,
        variant,
        name,
        channelMembers,
        channelManagers,
        channelId,
        isEdit,
        communityId,
        loading: isLoading,
        isPublic,
        initialValues,
        setName,
        setIsPublic,
        setChannelMembers,
        setChannelManagers,
        handleOpenMain,
        handleOpenMembers,
        handleOpenChanelManager,
        onEditSuccess
      })}
    </>
  )
}
